<template>
  <div
    class="project-item"
    :class="{ 'with-path': showPath }"
    :style="itemStyle"
    @dblclick="toDesign(item)"
    tabindex="1"
  >
    <div class="preview" v-html="props.graphicalPreview"></div>
    <div v-if="framePreview" class="preview absolute top-0 left-0" v-html="framePreview"></div>
    <div class="item-name flex flex-col">
      <div class="project-item-name">{{ item.name }}</div>
      <div class="item-date">{{ item.pageLatestUpdatedTime }}</div>
      <div v-if="showPath" class="item-path">{{ path }}</div>
    </div>
    <div class="actions">
      <div class="star h-[16px] text-base" @click="emit('star')">
        <star-filled style="color: rgb(250, 219, 20)" v-if="item.favoriteFlag" />
        <star-outlined class="unstarred" v-else />
      </div>
      <Menu
        v-if="projectMenus.length"
        :menus="projectMenus"
        @click="key => handleProjectMenuClick(key, item)"
      >
        <Icon
          class="ml-1 more"
          code="xe719;"
          :gutter="0"
          tabindex="1"
          :size="18"
          @click.stop=""
        ></Icon>
      </Menu>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { Modal, Menu } from '@elecmap/elecmap-ui'
import type { EmMenuItem } from '@elecmap/elecmap-ui/lib/menu/types'
import { Icon, renderIcon } from '@elecmap/icon'
import type { CadProjectShortVo } from '@elecmap/api/src/cad/space.d'
import { isDev } from '@elecmap/api/src/env'
import { StarOutlined, StarFilled, StarTwoTone, HeartFilled } from '@ant-design/icons-vue'
import { cachedFramePreview, getFrameById } from '@/store/frame'
import type { MemberPermission } from '@elecmap/api/src/cad/shared.d'
import { getDisabledProjectPermission } from '@elecmap/shared/src/utils/permission'
import userStore from '@/store/user'

const props = withDefaults(
  defineProps<{
    item: CadProjectShortVo
    graphicalPreview?: string
    frameId?: number
    showPath?: boolean
    width?: number
    height?: number
    spaceRole?: MemberPermission
    spaceType?: string
  }>(),
  {
    showPath: false,
  },
)

const us = userStore()

const framePreview = computed(() =>
  props.frameId ? cachedFramePreview.value[props.frameId] : undefined,
)

const itemStyle = computed(() => {
  if (!props.width) return
  return {
    width: `${props.width}px`,
    height: `${props.height}px`,
  }
})

const previewStyle = computed(() => {
  if (!props.width) return
  return {}
})

watch(
  () => props.frameId,
  id => {
    if (!id) return
    getFrameById(id)
  },
  { immediate: true },
)

const path = computed(() => {
  if (!props.showPath) return ''
  return props.item.parentFolderList.map(pf => pf.name).join(' > ')
})
const emit = defineEmits<{
  (event: 'rename', payload: { id: number; name: string }): void
  (event: 'copy', payload: { id: number; name: string }): void
  (event: 'delete'): void
  (event: 'permission'): void
  (event: 'move'): void
  (event: 'star'): void
}>()

const projectMenus = computed<EmMenuItem[][]>(() => {
  // if (us.isViewAccount) {
  //   return []
  // }
  const disableKeys = getDisabledProjectPermission(
    props.spaceRole,
    props.item.role,
    us.canManage,
    props.spaceType,
  )
  return [
    [
      {
        name: '开始设计',
        key: 'toDesign',
        icon: renderIcon({ type: 'cmStart' }),
        disabled: us.isViewAccount,
      },
    ].filter(m => {
      return !disableKeys.includes(m.key)
    }),
    [
      {
        name: '重命名',
        key: 'rename',
        icon: renderIcon({ type: 'cmEdit' }),
      },
      {
        name: '权限',
        key: 'permission',
        icon: renderIcon({ type: 'cmPermission' }),
      },
      {
        name: '复制',
        key: 'copy',
        icon: renderIcon({ type: 'cmCopy' }),
      },
      {
        name: '移动',
        key: 'move',
        icon: renderIcon({ type: 'cmMove' }),
      },
      // {
      //   name: '导出',
      //   key: 'export',
      //   icon: renderIcon({ type: 'cmExport' }),
      // },
      // {
      //   name: '分享协作',
      //   key: 'share',
      //   icon: renderIcon({ type: 'cmShare' }),
      // },
    ].filter(m => {
      return !disableKeys.includes(m.key)
    }),
    [
      {
        name: '删除',
        key: 'delete',
        icon: renderIcon({ type: 'cmDelete' }),
      },
    ].filter(() => {
      return !disableKeys.includes('delete')
    }),
  ].filter(item => item.length)
})

function handleRenameProject(project: CadProjectShortVo) {
  Modal.form({
    width: '520px',
    title: `重命名-${project.name}`,
    formItems: [
      {
        label: '名称',
        placeholder: '请输入名称',
        type: 'Input',
        key: 'name',
        rules: [
          {
            required: true,
            message: '请输入名称',
          },
        ],
      },
    ],
    formState: reactive({ keywords: { ...project }, tags: {} }),
    okText: '确认',
    onSubmit: ({ keywords }: any) => {
      emit('rename', { ...keywords })
    },
  })
}

function handleCopyProject(project: CadProjectShortVo) {
  Modal.form({
    width: '520px',
    title: `复制-${project.name}`,
    formItems: [
      {
        label: '名称',
        placeholder: '请输入名称',
        type: 'Input',
        key: 'name',
        rules: [
          {
            required: true,
            message: '请输入名称',
          },
        ],
      },
    ],
    formState: reactive({ keywords: { ...project, name: `${project.name} - 副本` }, tags: {} }),
    okText: '确认',
    onSubmit: ({ keywords }: any) => {
      emit('copy', { ...keywords })
    },
  })
}

function handleDeleteProject(project: CadProjectShortVo) {
  Modal.confirm({
    title: `删除-${project.name}`,
    content: [
      '请确认您要删除这个项目。',
      '这个项目内相关信息将会被永久删除。',
      '这是一个不可逆的操作，请谨慎对待。',
    ].join('<br />'),
    onOk: () => {
      emit('delete')
    },
  })
}

function toDesign(p: CadProjectShortVo) {
  if (us.isViewAccount) {
    const url = isDev ? `http://127.0.0.1:8084?id=${p.id}` : `/view?id=${p.id}`
    window.location.href = url
    return
  }
  const disabledKeys = getDisabledProjectPermission(
    props.spaceRole,
    props.item.role,
    us.canManage,
    props.spaceType,
  )
  const path = disabledKeys.includes('toDesign') ? 'view' : 'design'
  const url = isDev ? `http://127.0.0.1:8083?id=${p.id}` : `/${path}?id=${p.id}`
  window.location.href = url
}

function handleProjectMenuClick(key: string, item: CadProjectShortVo) {
  switch (key) {
    case 'toDesign':
      toDesign(item)
      break
    case 'rename':
      handleRenameProject(item)
      break
    case 'copy':
      handleCopyProject(item)
      break
    case 'permission':
      emit('permission')
      break
    case 'move':
      emit('move')
      break
    case 'delete':
      handleDeleteProject(item)
      break
  }
}
</script>

<style lang="less">
.project-item {
  position: relative;
  cursor: pointer;
  width: 318px;
  height: 270px;
  margin-right: 36px;
  margin-bottom: 52px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  background-color: #fafafa;
  overflow: hidden;
  &.with-path {
    margin-bottom: 58px;
  }
  &:focus {
    outline: 2px solid #268feb;
    .more {
      opacity: 1;
    }
    .star {
      .unstarred {
        opacity: 1;
      }
    }
    box-shadow: 1px 1px 2px 2px rgb(240, 240, 240);
  }
  &.create {
    color: #999;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(0, 0, 0, 0.15);
    // box-shadow: 1px 1px 2px 2px rgb(240, 240, 240);
    border-radius: 4px;
    transition: box-shadow 0.2s;
    .text {
      margin-right: 4px;
    }
  }
  .preview {
    // position: relative;
    // box-shadow: 1px 1px 2px 2px rgb(240, 240, 240);
    width: 100%;
    height: calc(100% - 47px) !important;
    padding: 4px 8px;
    transition: box-shadow 0.2s;
  }

  .actions {
    position: absolute;
    right: 8px;
    bottom: 12px;
    display: flex;
    align-items: center;
    .icon {
      height: 12px;
    }
  }

  .item-name {
    padding: 3px 8px 0;
    height: 47px;
    background-color: white;
    border-top: 1px solid rgba(0, 0, 0, 0.15);
    .project-item-name {
      font-weight: 600;
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .item-date {
      color: rgba(32, 32, 32, 0.5);
    }
  }

  .more {
    cursor: pointer;
    transition: opacity 0.18s ease-in-out;
    opacity: 0;
    width: 20px;
    height: 20px;
    padding-left: 1px;
    &:hover {
      background-color: #f0f0f0;
    }
  }
  .star {
    // margin-left: 2px;
    width: 20px;
    height: 20px;
    padding-top: 1px;
    text-align: center;
    .unstarred {
      transition: opacity 0.18s ease-in-out;
      opacity: 0;
    }
    &:hover {
      background-color: #f0f0f0;
    }
  }

  &:hover {
    .more {
      opacity: 1;
    }
    .star {
      .unstarred {
        opacity: 1;
      }
    }
    box-shadow: 1px 1px 2px 2px rgb(240, 240, 240);
    &.create {
      box-shadow: 1px 1px 2px 2px rgb(240, 240, 240);
    }
  }

  .more:focus {
    opacity: 1;
  }

  .item-path {
    position: absolute;
    bottom: -44px;
    left: 0px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
  }
}
</style>
